// gradeModel.js — the single source of truth for grade ↔ graduation-year math.
//
// Why this exists: a student is persistent across years, but "grade" is not — it
// advances every school year. Storing a static `grade` field means someone has to
// bump it manually each fall (and the importer doesn't, see IDEAS.md). Instead we
// store a stable **graduation year** (`gradYear`, the "Class of YYYY" cohort) and
// DERIVE the grade from it + the current school year. A 5th grader this year is a
// 6th grader next year with zero writes.
//
// `gradYear` is anchored to the universal 12th-grade graduation year so it's
// school-independent (portable if a student transfers). A school's grade-span only
// decides which grades show in pickers and when a student "graduates OUT" of THAT
// school — it does not change the cohort number.
//
// Pure functions only — no Firebase, no React. Easy to reason about and test.

// Canonical grade labels the rest of the app already uses.
export const GRADES = [
  "Pre-K", "Kindergarten",
  "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12",
];

// Ordinal scale used for all math: Pre-K = -1, Kindergarten = 0, 1..12 = 1..12.
// (So "years until 12th-grade graduation" = 12 - ordinal.)
const ALIASES = {
  PREK: "Pre-K", "PRE-K": "Pre-K", "PRE K": "Pre-K", PK: "Pre-K",
  K: "Kindergarten", KG: "Kindergarten", KINDER: "Kindergarten", KINDERGARTEN: "Kindergarten",
};

// Tolerant canonicalizer: accepts "K", "PK", "1st", "Kindergarten", 5, etc.
export function canonicalGrade(raw) {
  if (raw === 0) return "Kindergarten";
  const g = String(raw ?? "").trim();
  if (!g) return "";
  const up = g.toUpperCase();
  if (ALIASES[up]) return ALIASES[up];
  const ordinal = up.match(/^(\d{1,2})(ST|ND|RD|TH)$/); // "1st" → "1"
  if (ordinal) return ordinal[1];
  return g;
}

export function gradeToOrdinal(grade) {
  const g = canonicalGrade(grade);
  if (g === "Pre-K") return -1;
  if (g === "Kindergarten") return 0;
  const n = parseInt(g, 10);
  return Number.isFinite(n) ? n : null;
}

export function ordinalToGrade(ord) {
  if (ord == null) return "";
  if (ord <= -1) return "Pre-K";
  if (ord === 0) return "Kindergarten";
  if (ord >= 1 && ord <= 12) return String(ord);
  return ""; // beyond 12 = graduated; no grade label
}

// The "ending" calendar year of the school year containing `date`. We roll on
// Aug 1: from August onward we're in the year that ends next summer. So Sept 2025
// and June 2026 both belong to the school year ending 2026.
export function currentSchoolYearEnd(date = new Date()) {
  const y = date.getFullYear();
  return date.getMonth() >= 7 /* Aug (0-based) */ ? y + 1 : y;
}

// grade (now) → graduation year (the spring they'd finish 12th grade).
export function gradeToGradYear(grade, schoolYearEnd = currentSchoolYearEnd()) {
  const ord = gradeToOrdinal(grade);
  if (ord == null) return null;
  return schoolYearEnd + (12 - ord);
}

// graduation year → grade ordinal (now). Inverse of the above.
export function gradYearToOrdinal(gradYear, schoolYearEnd = currentSchoolYearEnd()) {
  if (gradYear == null) return null;
  return 12 - (gradYear - schoolYearEnd);
}

// graduation year → canonical grade label (now). "" once past 12th grade.
export function gradYearToGrade(gradYear, schoolYearEnd = currentSchoolYearEnd()) {
  return ordinalToGrade(gradYearToOrdinal(gradYear, schoolYearEnd));
}

// "Class of 2033"
export function classOfLabel(gradYear) {
  return gradYear == null ? "" : `Class of ${gradYear}`;
}

// The grade to SHOW for a student: derive from gradYear when present (auto-advances
// each year), else fall back to the legacy static `grade` field. Safe for both old
// (no gradYear) and new students.
export function studentGrade(student, schoolYearEnd = currentSchoolYearEnd()) {
  if (student && student.gradYear != null) {
    return gradYearToGrade(student.gradYear, schoolYearEnd) || canonicalGrade(student.grade);
  }
  return canonicalGrade(student?.grade);
}

// The fields to WRITE when creating/updating a student's grade: keep the static
// `grade` (back-compat + the picked value) AND the derived `gradYear` cohort.
export function gradeFields(grade, schoolYearEnd = currentSchoolYearEnd()) {
  const canonical = canonicalGrade(grade);
  const gradYear = gradeToGradYear(canonical, schoolYearEnd);
  return gradYear == null ? { grade: canonical } : { grade: canonical, gradYear };
}

// ── School grade-span config ────────────────────────────────────────────────
// A school's span bounds the grade picker and decides who graduates OUT.
export const GRADE_SPAN_PRESETS = [
  { key: "K-5",  label: "Elementary (K–5)",      low: "Kindergarten", high: "5" },
  { key: "K-6",  label: "Elementary (K–6)",      low: "Kindergarten", high: "6" },
  { key: "PK-5", label: "Elementary (Pre-K–5)",  low: "Pre-K",        high: "5" },
  { key: "K-8",  label: "K–8",                   low: "Kindergarten", high: "8" },
  { key: "6-8",  label: "Middle (6–8)",          low: "6",            high: "8" },
  { key: "9-12", label: "High (9–12)",           low: "9",            high: "12" },
  { key: "K-12", label: "K–12",                  low: "Kindergarten", high: "12" },
];

// Canonical grades within a span (inclusive), in order — for grade pickers.
export function gradesInSpan(low, high) {
  const lo = gradeToOrdinal(low);
  const hi = gradeToOrdinal(high);
  if (lo == null || hi == null) return GRADES;
  return GRADES.filter((g) => {
    const o = gradeToOrdinal(g);
    return o >= lo && o <= hi;
  });
}

// Has this student aged out of the school's top grade? Needs the span's high grade.
// Derived grade ordinal > span-high ordinal ⇒ graduated out.
export function hasGraduated(gradYear, spanHigh, schoolYearEnd = currentSchoolYearEnd()) {
  const ord = gradYearToOrdinal(gradYear, schoolYearEnd);
  const hi = gradeToOrdinal(spanHigh);
  if (ord == null || hi == null) return false;
  return ord > hi;
}
